import type { ItemId } from "./items.js";
import type { SkillId } from "./skills.js";

export const DAILY_TZ = "America/Chicago";

export type DailyDifficulty = "easy" | "medium" | "hard";
export type LampTier = "small" | "medium" | "large" | "huge";

export const LAMP_MULT: Record<LampTier, number> = {
  small: 10,
  medium: 25,
  large: 50,
  huge: 100,
};

export interface DailyTaskDef {
  id: string;
  kind: "gather" | "craft";
  itemId: ItemId;
  target: number;
  difficulty: DailyDifficulty;
  label: string;
}

export const DAILY_TASKS: readonly DailyTaskDef[] = [
  { id: "gather-pebble", kind: "gather", itemId: "pebble", target: 8, difficulty: "easy", label: "Dig 8 Pebbles" },
  { id: "gather-bone", kind: "gather", itemId: "bone_shard", target: 8, difficulty: "easy", label: "Dig 8 Bones" },
  { id: "gather-twig", kind: "gather", itemId: "twig", target: 8, difficulty: "easy", label: "Fetch 8 Twigs" },
  { id: "craft-bronze", kind: "craft", itemId: "bronze_treat", target: 2, difficulty: "easy", label: "Make 2 Bronze Treats" },
  { id: "gather-iron", kind: "gather", itemId: "iron_nugget", target: 5, difficulty: "medium", label: "Dig 5 Iron" },
  { id: "gather-stick", kind: "gather", itemId: "stick", target: 4, difficulty: "medium", label: "Fetch 4 Sticks" },
  { id: "craft-iron", kind: "craft", itemId: "iron_treat", target: 2, difficulty: "medium", label: "Make 2 Iron Treats" },
  { id: "gather-charcoal", kind: "gather", itemId: "charcoal", target: 4, difficulty: "medium", label: "Dig 4 Charcoal" },
  { id: "gather-hardwood", kind: "gather", itemId: "hardwood_stick", target: 3, difficulty: "hard", label: "Fetch 3 Hardwood" },
  { id: "craft-steel", kind: "craft", itemId: "steel_treat", target: 2, difficulty: "hard", label: "Make 2 Steel Treats" },
  { id: "gather-silver", kind: "gather", itemId: "silver_tag", target: 3, difficulty: "hard", label: "Dig 3 Silver" },
];

export interface DailyChallengeState {
  dateKey: string;
  taskId: string;
  progress: number;
  targetCount: number;
  completed: boolean;
  claimed: boolean;
  streakCount: number;
  lampIndex: number;
  completedDates: string[];
  hudLayout: { x: number; y: number; scale: number };
}

export interface DailyLoginState {
  streak: number;
  lastClaimDateKey: string | null;
  lastClaimAt: number;
}

export function chicagoDateKey(now = Date.now()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: DAILY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(now));
}

export function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function pickDailyTask(userId: string, dateKey: string, difficulty: DailyDifficulty): DailyTaskDef {
  const pool = DAILY_TASKS.filter((task) => task.difficulty === difficulty);
  const list = pool.length > 0 ? pool : DAILY_TASKS;
  const index = hashString(`${userId}:${dateKey}`) % list.length;
  return list[index]!;
}

export function streakInfo(streakCount: number): {
  difficulty: DailyDifficulty;
  lamp: LampTier;
  xpMult: number;
} {
  if (streakCount <= 1) {
    return { difficulty: "easy", lamp: "small", xpMult: 1 };
  }
  if (streakCount <= 3) {
    return { difficulty: "easy", lamp: streakCount === 3 ? "medium" : "small", xpMult: 10 };
  }
  if (streakCount <= 5) {
    return { difficulty: "medium", lamp: "medium", xpMult: 25 };
  }
  if (streakCount === 6) {
    return { difficulty: "hard", lamp: "large", xpMult: 50 };
  }
  return { difficulty: "hard", lamp: "huge", xpMult: 100 };
}

export function previousDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const utc = Date.UTC(y!, m! - 1, d!);
  return chicagoDateKey(utc - 36 * 3600 * 1000);
}

export function emptyDailyChallenge(userId: string, now = Date.now()): DailyChallengeState {
  const dateKey = chicagoDateKey(now);
  const info = streakInfo(1);
  const task = pickDailyTask(userId, dateKey, info.difficulty);
  return {
    dateKey,
    taskId: task.id,
    progress: 0,
    targetCount: task.target,
    completed: false,
    claimed: false,
    streakCount: 1,
    lampIndex: 0,
    completedDates: [],
    hudLayout: { x: 12, y: 12, scale: 1 },
  };
}

export function emptyDailyLogin(): DailyLoginState {
  return { streak: 0, lastClaimDateKey: null, lastClaimAt: 0 };
}

export function lampXp(skillLevel: number, streakCount: number): number {
  return skillLevel * streakInfo(streakCount).xpMult;
}

export function achievementLampXp(skillLevel: number, tier: LampTier): number {
  return skillLevel * LAMP_MULT[tier];
}

export function dailyTaskById(id: string): DailyTaskDef | undefined {
  return DAILY_TASKS.find((task) => task.id === id);
}

export function loginReward(day: number): { itemId: ItemId; quantity: number } {
  const rewards: { itemId: ItemId; quantity: number }[] = [
    { itemId: "elixir_of_the_deep_mint", quantity: 1 },
    { itemId: "scroll_of_timber_surge", quantity: 1 },
    { itemId: "volcanic_forgemaster_draught", quantity: 1 },
    { itemId: "elixir_of_the_deep_mint", quantity: 1 },
    { itemId: "scroll_of_timber_surge", quantity: 1 },
    { itemId: "volcanic_forgemaster_draught", quantity: 1 },
    { itemId: "scroll_of_hospitality", quantity: 1 },
  ];
  return rewards[Math.max(0, Math.min(6, day - 1))]!;
}

/** Lamp XP multipliers shown on the weekly challenge track. */
export const DAILY_LAMP_TRACK: readonly { day: number; mult: number }[] = [
  { day: 1, mult: 1 },
  { day: 2, mult: 10 },
  { day: 4, mult: 25 },
  { day: 6, mult: 50 },
  { day: 7, mult: 100 },
];

/** Milliseconds until next America/Chicago midnight (daily reset). */
export function msUntilDailyReset(now = Date.now()): number {
  const today = chicagoDateKey(now);
  const [y, m, d] = today.split("-").map(Number);
  // Probe forward until the Chicago calendar day flips.
  let guess = now + 60_000;
  for (let i = 0; i < 48; i++) {
    if (chicagoDateKey(guess) !== today) {
      // Binary-ish refine to the flip second.
      let lo = now;
      let hi = guess;
      while (hi - lo > 250) {
        const mid = Math.floor((lo + hi) / 2);
        if (chicagoDateKey(mid) === today) {
          lo = mid;
        } else {
          hi = mid;
        }
      }
      return Math.max(0, hi - now);
    }
    guess += 30 * 60_000;
  }
  // Fallback: 24h from "noon" of the parsed date in UTC approximation.
  void y;
  void m;
  void d;
  return 24 * 3600 * 1000;
}

export function formatDailyCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Last `count` Chicago date keys ending on `endKey` (inclusive), oldest first. */
export function recentDateKeys(endKey: string, count: number): string[] {
  const keys: string[] = [endKey];
  let cur = endKey;
  for (let i = 1; i < count; i++) {
    cur = previousDateKey(cur);
    keys.unshift(cur);
  }
  return keys;
}

/** Single letter weekday for a YYYY-MM-DD key in America/Chicago. */
export function chicagoWeekdayLetter(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  // Noon UTC on that civil date keeps the Chicago weekday stable.
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: DAILY_TZ,
    weekday: "narrow",
  }).format(new Date(Date.UTC(y!, m! - 1, d!, 17)));
  return label;
}

export type SkillIdForLamp = SkillId;
